/**
 * Which set of people a whereabouts view is showing.
 *
 * - `followed`: the signed-in user's followed teams and workgroups (the default
 *   for /whereabouts/team).
 * - `team` / `workgroup`: exactly one group, pinned by the URL. Used by the
 *   company team / workgroup pages and the search-results page.
 *
 * Pure helpers only; no React or Redux imports so this is safe to use from ducks.
 */
export type WhereaboutsScope =
  | { kind: 'followed' }
  | { kind: 'team'; teamId: string }
  | { kind: 'workgroup'; workGroupId: number };

export const FOLLOWED_SCOPE: WhereaboutsScope = { kind: 'followed' };

export function scopeKey(scope: WhereaboutsScope): string {
  switch (scope.kind) {
    case 'team': return `team:${scope.teamId}`;
    case 'workgroup': return `workgroup:${scope.workGroupId}`;
    default: return 'followed';
  }
}

export function isFollowedScope(scope: WhereaboutsScope): scope is { kind: 'followed' } {
  return scope.kind === 'followed';
}

export interface GroupIds {
  teamIds: string[];
  workGroupIds: number[];
}

// The explicit group ids a scope resolves to, given the user's followed ids.
export function scopeToGroupIds(scope: WhereaboutsScope, followed: GroupIds): GroupIds {
  switch (scope.kind) {
    case 'team': return { teamIds: [scope.teamId], workGroupIds: [] };
    case 'workgroup': return { teamIds: [], workGroupIds: [scope.workGroupId] };
    default: return followed;
  }
}

// Drops the given user from a member list. The whereabouts views render the
// signed-in user in their own section, so group lists must not repeat them.
export function excludeUser<T extends { id: string }>(members: T[], userId?: string): T[] {
  if (!userId) return members.filter(Boolean);
  return members.filter(m => m && m.id !== userId);
}
